import type {
  CourseStructureFontSchemaType,
  CourseStructureSchemaType,
} from '@vedalib/editor/lib/brand'
import type { CourseStructureElementValue, CourseStructureItem } from '@vedalib/editor/lib/elements'
import React from 'react'

import Highlighter from 'components/uiKit/Highlighter'
import type { KeyIconsType } from 'components/uiKit/Icon'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import type { BrandType, ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { useProjectT } from 'services/ProjectLabels'
import { scrollProps } from 'services/Scroll/ScrollService'
import { BlockMode, SectionTypeEnum } from 'services/Store/Project/enums'
import { testPropsEl } from 'utils/test/qaData'

import * as s from './StructureItemElement.module.scss'

type SectionConfigType = {
  [key in SectionTypeEnum]: {
    icon: KeyIconsType
    getName: (pt: (key: keyof BrandType['labels']) => string) => string
    getImage: (styles: ElementStyleCss<CourseStructureSchemaType>) => string | null | undefined
    getShowIcon: (value: CourseStructureElementValue) => boolean
    countDetails: (
      item: CourseStructureItem,
      pt: (key: keyof BrandType['labels']) => string,
      childCount?: number,
    ) => string
  }
}

export const sectionConfig: SectionConfigType = {
  [SectionTypeEnum.landing]: {
    icon: 'contentsLanding' as const,
    getName: (pt: (key: keyof BrandType['labels']) => string) => pt('courseStructure.landing'),
    getImage: (styles: ElementStyleCss<CourseStructureSchemaType>) =>
      styles?.sectionIcons.backgroundImage,
    countDetails: (
      _item: CourseStructureItem,
      pt: (key: keyof BrandType['labels']) => string,
      sectionCount?: number,
    ) => `${sectionCount} ${pt('courseStructure.sections')}`,
    getShowIcon: (value: CourseStructureElementValue) => value.showLandingIcon,
  },
  [SectionTypeEnum.test]: {
    icon: 'contentsTest' as const,
    getName: (pt: (key: keyof BrandType['labels']) => string) => pt('courseStructure.test'),
    getImage: (styles: ElementStyleCss<CourseStructureSchemaType>) =>
      styles?.testIcons.backgroundImage,
    countDetails: (item: CourseStructureItem, pt: (key: keyof BrandType['labels']) => string) =>
      `${item.blocks.filter(({ mode }) => mode === BlockMode.questions).length} ${pt('courseStructure.questions')}`,
    getShowIcon: (value: CourseStructureElementValue) => value.showTestIcon,
  },
  [SectionTypeEnum.chapter]: {
    icon: 'contentsChapter' as const,
    getName: (pt: (key: keyof BrandType['labels']) => string) => pt('courseStructure.chapter'),
    getImage: (styles: ElementStyleCss<CourseStructureSchemaType>) =>
      styles?.chapterIcons.backgroundImage,
    countDetails: (
      _item: CourseStructureItem,
      pt: (key: keyof BrandType['labels']) => string,
      sectionCount?: number,
    ) => `${sectionCount} ${pt('courseStructure.sections')}`,
    getShowIcon: (value: CourseStructureElementValue) => value.showChapterIcon,
  },
  [SectionTypeEnum.cover]: {
    icon: 'contentsSlides' as const,
    getName: (pt: (key: keyof BrandType['labels']) => string) => pt('courseStructure.cover'),
    getImage: (_styles: ElementStyleCss<CourseStructureSchemaType>) => undefined,
    countDetails: (
      _item: CourseStructureItem,
      pt: (key: keyof BrandType['labels']) => string,
      sectionCount?: number,
    ) => `${sectionCount} ${pt('courseStructure.sections')}`,
    getShowIcon: (_value: CourseStructureElementValue) => false,
  },
}

interface IItemsProps {
  item: CourseStructureItem
  searchValue?: string
  font: ElementFontCss<CourseStructureFontSchemaType>
  styles: ElementStyleCss<CourseStructureSchemaType>
  showIcon?: boolean
  marginLeft?: number
  disabled?: boolean
}

const StructureItemElement: React.FC<IItemsProps> = ({
  item,
  searchValue,
  font,
  styles,
  showIcon,
  marginLeft,
  disabled,
}) => {
  const pt = useProjectT()
  const { getImage, icon } = sectionConfig[item.type]
  const iconBg = getImage(styles)
  const titleStyle = item.isChapter ? font?.chapter : font?.section
  const styleFontSection = { ...titleStyle, ...(disabled && { color: styles.item.color }) }
  const iconStyles = {
    ...(showIcon && iconBg && { backgroundImage: iconBg }),
    marginRight: styles.offsetFromIcon.marginRight,
    marginLeft,
  }

  return (
    <>
      {showIcon && (
        <div className={s.iconImage} style={iconStyles}>
          {!iconBg && <Icon name={icon} size={KitSize.S} aria-hidden />}
        </div>
      )}
      <div
        {...testPropsEl('sectionItem', { label: item.name }, { id: item.id })}
        {...scrollProps(item.id)}
        aria-disabled={disabled}
        aria-label={`Navigate to ${item.name || pt('courseStructure.chapterName')}`}
        className={s.item}
        style={{ color: titleStyle?.color, ...(!showIcon && { marginLeft }) }}
      >
        <div className={s.itemContent}>
          <div style={styleFontSection}>
            {item.name ? (
              <Highlighter search={searchValue} text={item.name} />
            ) : (
              pt('courseStructure.chapterName')
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default StructureItemElement
